import { AuthErrorKind, AuthFlow, AuthFlowError, Locales } from '@griply/types';
import { ErrorLevel } from '@griply/utils';
import {
  getAdditionalUserInfo,
  OAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import i18n from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { getErrorReporter } from 'shared/services/error-tracking';
import {
  finishGoogleSignIn,
  getSearchParamGoogleIdToken,
  hasMatchingSignInNonce,
  removeGoogleIdTokenFromUrl,
} from 'shared/services/google';
import {
  clearNonce,
  clearOnboardingAnswers,
  getNonce,
} from 'shared/services/local-storage';
import {
  getSearchParamIdToken,
  removeIdTokenFromUrl,
} from 'shared/utils/get-search-param-id-token';

import { finishUserSignup } from './finish-user-signup';
import { getUser } from './get-user';
import { getAuthentication, handleAuthError } from './helpers';
import { channelLogin } from './helpers/auth';
import { processOnboardingAnswers } from './process-onboarding-answers';
import { setPushTokenOnUser } from './set-push-token-on-user';
import { setUserCountry } from './set-user-country';

type AppleJwt = {
  iss?: string;
  user?: { firstName: string; lastName: string };
  state?: string;
};

const signInWithApple = async (
  token: string,
): Promise<AuthFlowError | null> => {
  // get the nonce used for sign-in
  const nonce = getNonce();
  const rawNonce = nonce?.raw;
  // Apple rejects a second use of the credential, and a reload would first sign the user out to replay it.
  removeIdTokenFromUrl();

  try {
    const decryptedToken = jwtDecode<AppleJwt>(token);
    const { user, state } = decryptedToken;

    const auth = await getAuthentication();

    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: token,
      rawNonce,
    });

    const result = await signInWithCredential(auth, credential);
    clearNonce();
    const isSignup = getAdditionalUserInfo(result)?.isNewUser ?? false;

    // if signup, finish signup
    if (isSignup) {
      await finishUserSignup({
        uid: auth.currentUser?.uid,
        name: user ? `${user?.firstName} ${user.lastName}` : undefined,
        locale: (i18n.resolvedLanguage ?? i18n.language) as Locales,
        queryParameters: state ? JSON.parse(state) : undefined,
      });

      // Process onboarding answers after signup
      await processOnboardingAnswers();
      clearOnboardingAnswers();
    }
  } catch (error) {
    return handleAuthError(error, AuthFlow.AppleSignIn, {
      nonce: nonce?.hashed,
      hasRawNonce: !!rawNonce,
      hasToken: !!token,
    });
  }

  return null;
};

const signInWithGoogle = async (
  token: string,
): Promise<AuthFlowError | null> => {
  try {
    await finishGoogleSignIn(token);
  } catch (error) {
    return handleAuthError(error, AuthFlow.GoogleSignIn);
  }

  return null;
};

// Resolves the failure the page should show, or null when there was nothing to sign in or it worked.
export const signInWithToken = async (): Promise<AuthFlowError | null> => {
  const googleIdToken = getSearchParamGoogleIdToken();
  const appleIdToken = getSearchParamIdToken();

  const idToken = googleIdToken ?? appleIdToken;

  if (!idToken) {
    return null;
  }

  // Nothing else ties the token in the URL to a sign-in this browser started — both providers echo the hashed nonce back.
  if (!hasMatchingSignInNonce(idToken)) {
    removeGoogleIdTokenFromUrl();
    removeIdTokenFromUrl();
    getErrorReporter().captureMessage(
      'third-party sign-in rejected: token carries no matching nonce',
      ErrorLevel.Error,
      {
        tags: { source: 'third-party-sign-in-nonce' },
        extra: {
          hasStoredNonce: !!getNonce(),
          provider: googleIdToken ? 'google' : 'apple',
        },
        fingerprint: ['third-party-sign-in-nonce'],
      },
    );
    return new AuthFlowError(AuthErrorKind.Unknown);
  }

  const auth = await getAuthentication();
  // Runs only once the token is known to belong to this browser, so a crafted link cannot sign anyone out.
  await auth.signOut();

  const signInError = googleIdToken
    ? await signInWithGoogle(googleIdToken)
    : await signInWithApple(idToken);

  if (signInError) {
    return signInError;
  }

  try {
    const user = await getUser();

    if (!user) {
      return null;
    }

    await setPushTokenOnUser(user.uid, user.settings?.language || Locales.EnUS);
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'set-push-token-after-third-party-sign-in' },
    });
  }

  try {
    await setUserCountry();
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'set-user-country-after-third-party-sign-in' },
    });
  }

  // log other tabs in as well
  channelLogin();

  return null;
};
